import { sphereColors } from "@styles/common-colors";
import { Box, SvgIcon, Collapse } from "@mui/material";
import { FaroIconButton } from "@components/common/faro-icon-button";

import FailedIcon from "@assets/icons/failed_32px.svg?react";
import WarningIcon from "@assets/icons/warning_32px.svg?react";
import ArrowUpIcon from "@assets/icons/new/arrow-up_10px.svg?react";
import ArrowDownIcon from "@assets/icons/new/arrow-down_10px.svg?react";
import { DataManagementTable } from "@pages/data-management/data-management-table";
import { WorkflowState, TableItem } from "@pages/data-management/data-management-types";

/**
 * Type for the different tables that could be visible.
 * - "mainTable"        - Main table which shows all scans that are not
 *                        in the ERROR or UNSUPPORTED tables.
 * - "errorTable"       - Table with scans that failed to process.
 * - "unsupportedTable" - Table with scans that are not supported yet.
 */
export type TableType = "mainTable" | "errorTable" | "unsupportedTable";


interface Props {
  /** Type of the table. Should not be "mainTable". */
  tableType: TableType

  /** Array of DataManagementTableItem entities. */
  items: TableItem[];

  /** Workflow state. */
  state: WorkflowState;

  /** Callback to set the scans the user selected for removal. */
  setScansToRemove: React.Dispatch<React.SetStateAction<TableItem[]>>

  /** Callback to set whether the confirmation dialog for removing scans is open. */
  setIsRemoveScansDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

  /** Filter on table items */
  filter?: (item: TableItem) => boolean;

  /** Callback to toggle between success and error states */
  setVisibleTableType: (table: TableType) => void;

  /** Whether to show success state */
  visibleTableType: TableType;

  /** Callback when a new table is about to be displayed by Collapse */
  onEnterTable: (table: TableType) => void;

  /** Callback when the old table has exited */
  onExitedTable: (table: TableType) => void;
}

/** Renders the table in the Staging Area that displays the scan items. */
export function DataManagementErrorTable({
  tableType,
  items,
  state,
  setScansToRemove,
  setIsRemoveScansDialogOpen,
  filter,
  setVisibleTableType,
  visibleTableType,
  onEnterTable,
  onExitedTable,

}: Props): JSX.Element {

  let tableBackground: string;
  let messageHeader: string = "";
  let messageText: JSX.Element = <p/>;
  let messageIcon: JSX.Element = <SvgIcon/>;
  switch (tableType) {
    case "mainTable":
      tableBackground = sphereColors.pureWhite;
      break;

    case "errorTable":
      tableBackground = sphereColors.red50;
      messageIcon = <SvgIcon inheritViewBox component={FailedIcon} />;
      messageHeader = "Scans failed to process";
      messageText = <p>
        Some scans couldn't be processed and must be removed before registration can be started.
      </p>;
      break;

    case "unsupportedTable":
      tableBackground = sphereColors.yellow100;
      messageIcon = <SvgIcon inheritViewBox component={WarningIcon} />;
      messageHeader = "Unsupported scans";
      messageText = <p>
        This project cannot be edited in Sphere XG because some of the scans are not supported.
        You can remove these scans and continue editing.<br />
        Alternatively, you can download the project to SCENE for editing and sync the
        updated project back to Sphere XG.
      </p>;
      break;
  }

  function toggleTable(e: React.MouseEvent): void {
    e.stopPropagation();
    if (visibleTableType === tableType) {
      setVisibleTableType("mainTable");
    } else {
      setVisibleTableType(tableType);
    }
  }

  return (
    <Box style={{ backgroundColor: tableBackground }}>
      <Box
        onClick={toggleTable}
        sx={{
          paddingX: "30px",
          paddingTop: "12px",
          minHeight: "52px",
          cursor: "pointer",
        }}
      >
        <span style={{
          marginRight: "100px",
          marginTop: "4px",
          fontSize: "larger",
          display: "inline-flex",
          gap: "8px",
        }}>
          {messageIcon}
          {messageHeader}
        </span>
        <span style={{
          marginTop: "-42px",
          marginRight: "-26px",
          flexDirection: "column",
          alignItems: "flex-end",
          display: "flex",
        }}>
          <FaroIconButton
            dataTestId="sa-error-table-toggle"
            onClick={toggleTable}
            component={visibleTableType === tableType ? ArrowUpIcon : ArrowDownIcon}
            iconButtonProps={{ sx: { margin: "5px" } }}
          />
        </span>
      </Box>

      <Collapse
        in={visibleTableType === tableType}
        unmountOnExit
        onEnter={() => onEnterTable(tableType)}
        onExited={() => onExitedTable(tableType)}
      >
        <Box sx={{ paddingX: "30px", paddingBottom: "30px" }} >
          {messageText}
          <DataManagementTable
            items={items}
            state={state}
            setScansToRemove={setScansToRemove}
            setIsRemoveScansDialogOpen={setIsRemoveScansDialogOpen}
            filter={filter}
            isTableEditable={false}
          />
        </Box>
      </Collapse>
    </Box>
  );
}